import {
  ArgumentError,
  getDifferenceBetweenWeekdays,
  DateAdapterBase as DateAdapterBase$1,
  dateInputToDateTime,
  dateInputToDateAdapter,
  normalizeDateTimeTimezone as normalizeDateTimeTimezone$1,
  InfiniteLoopError,
  dateTimeSortComparer,
  cloneRuleOptions,
  normalizeRuleOptions,
  RecurrenceRulesIterator,
  recurrenceRulesReducer,
  DateTime as DateTime$1,
} from '@rschedule/core';

class OccurrenceGenerator {
  constructor(args = {}) {
    this.timezone = args.timezone !== undefined ? args.timezone : null;
    this.maxDuration = args.maxDuration || 0;
  }
  /** Returns the first occurrence or, if there are no occurrences, null. */
  get firstDate() {
    if (this._firstDate !== undefined) return this._firstDate;
    const start = this._run().next().value;
    this._firstDate = start ? this.dateAdapter.fromDateTime(start) : null;
    return this._firstDate;
  }
  /** If generator is infinite, returns `null`. Otherwise returns the end date */
  get lastDate() {
    if (this._lastDate !== undefined) return this._lastDate;
    if (this.isInfinite) {
      this._lastDate = null;
      return null;
    }
    const end = this._run({ reverse: true }).next().value;
    this._lastDate = end ? this.dateAdapter.fromDateTime(end) : null;
    return this._lastDate;
  }
  pipe(...operators) {
    return operators.reduce((prev, curr) => curr({ base: prev, timezone: this.timezone }), this);
  }
  /**
     * Processes the object's rules/dates and returns an iterable for the occurrences.
     *
     * Options object:
     * - `start` the date to begin iteration on
     * - `end` the date to end iteration on
     * - `take` the max number of dates to take before ending iteration
     * - `reverse` whether to iterate in reverse or not
     *
     * Examples:
     *
     * ```
     * const iterator = schedule.occurrences({ start: new Date(), take: 5 });
     
     * for (const date of iterator) {
     *   // do stuff
     * }
  
     * iterator.toArray() // returns Date array
     * iterator.next().value // returns next Date
     * ```
     *
     */
  occurrences(args = {}) {
    return new OccurrenceIterator(this, this.normalizeOccurrencesArgs(args));
  }
  /**
   * Iterates over the object's occurrences and bundles them into collections
   * with a specified granularity (default is `"YEARLY"`). Make sure to
   * read about each option & combination of options below.
   *
   * Options object:
   *   - start?: DateAdapter
   *   - end?: DateAdapter
   *   - take?: number
   *   - reverse?: NOT SUPPORTED
   *   - granularity?: CollectionsGranularity
   *   - weekStart?: DateAdapter.Weekday
   *   - skipEmptyPeriods?: boolean
   *
   * Returned `Collection` object:
   *
   *   - `dates` property containing an array of DateAdapter objects.
   *   - `granularity` property containing the granularity.
   *     - `CollectionsGranularity` === `RuleOptions.Frequency`.
   *     - default is `"YEARLY"`
   *   - `periodStart` property containing a DateAdapter equal to the period's
   *     start time.
   *   - `periodEnd` property containing a DateAdapter equal to the period's
   *     end time.
   *
   * #### Details:
   *
   * `collections()` always returns full periods. This means that the `start` argument is
   * transformed to be the start of whatever period the `start` argument is in, and the
   * `end` argument is transformed to be the end of whatever period the `end` argument is
   * in.
   *
   * - Example: with granularity `"YEARLY"`, the `start` argument will be transformed to be the
   *   start of the year passed in the `start` argument, and the `end` argument will be transformed
   *   to be the end of the year passed in the `end` argument.
   *
   * By default, the `periodStart` value of `Collection` objects produced by this method increments linearly.
   * This means the returned `Collection#dates` property may have length 0. This can be changed by
   * passing the `skipEmptyPeriods: true` option, in which case the `periodStart` from one collection to the
   * next can "jump".
   *
   * - Example 1: if your object's first occurrence is 2019/2/1 (February 1st) and you call
   *   `collection({skipEmptyPeriods: true, granularity: 'DAILY', start: new Date(2019,0,1)})`
   *   (so starting on January 1st), the first Collection produced will have a `periodStart` in February.
   *
   * - Example 2: if your object's first occurrence is 2019/2/1 (February 1st) and you call
   *   `collection({granularity: 'DAILY', start: new Date(2019,0,1)})`
   *   (so starting on January 1st), the first collection produced will have a `Collection#periodStart`
   *   of January 1st and have `Collection#dates === []`. Similarly, the next 30 collections produced
   *   (Jan 2nd - 31st) will all contain an empty array for the `dates` property. Then the February 1st
   *   `Collection` will contain dates.
   *
   * When giving a `take` argument to `collections()`, you are specifying
   * the number of `Collection` objects to return (rather than occurrences).
   *
   * When choosing a granularity of `"WEEKLY"`, the `weekStart` option is required.
   *
   * When choosing a granularity of `"MONTHLY"`:
   *
   * - If the `weekStart` option *is not* present, will generate collections with
   *   the `periodStart` and `periodEnd` at the beginning and end of each month.
   *
   * - If the `weekStart` option *is* present, will generate collections with the
   *   `periodStart` equal to the start of the first week of the month, and the
   *   `periodEnd` equal to the end of the last week of the month. This behavior could be
   *   desired when rendering opportunities in a calendar view, where the calendar renders
   *   full weeks (which may result in the calendar displaying dates in the
   *   previous or next months).
   *
   */
  collections(args = {}) {
    return new CollectionIterator(this, this.normalizeCollectionsArgs(args));
  }
  /**
   * Returns true if an occurrence starts on or between the provided start/end
   * datetimes. If the `excludeEnds` option is provided, then occurrences
   * equal to the start/end times are ignored.
   *
   * If the occurrence generator has a duration, and `excludeEnds !== true`,
   * and a `maxDuration` argument is supplied (either in the constructor or
   * here), then any occurrence that's time overlaps with the start/end times
   * return true.
   */
  occursBetween(startInput, endInput, options = {}) {
    const start = this.normalizeDateInput(startInput);
    const end = this.normalizeDateInput(endInput);
    if (this.hasDuration && !options.excludeEnds) {
      const maxDuration = this.getMaxDuration('occursBetween', options);
      const iterator = this._run({
        start: start.subtract(maxDuration, 'millisecond'),
        end,
        reverse: true,
      });
      for (const day of iterator) {
        if (day.end.isBefore(start)) continue;
        return true;
      }
      return false;
    }
    for (const day of this._run({ start, end })) {
      if (options.excludeEnds) {
        if (day.isEqual(start)) {
          continue;
        }
        if (day.isEqual(end)) {
          break;
        }
      }
      return true;
    }
    return false;
  }
  occursOn(rawArgs) {
    const args = this.normalizeOccursOnArgs(rawArgs);
    if (args.weekday) {
      if (this.isInfinite && !args.before) {
        throw new ArgumentError(
          'When calling `occursOn()` with a `weekday` argument ' +
            'and an occurrence object that has infinite occurrences, ' +
            'you must include a `before` argument as well.',
        );
      }
      const start = args.after && (args.excludeEnds ? args.after.add(1, 'day') : args.after);
      const end = args.before && (args.excludeEnds ? args.before.subtract(1, 'day') : args.before);
      const iterator = this._run({ start, end });
      let date = iterator.next().value;
      if (!date) return false;
      while (date) {
        if (date.get('weekday') === args.weekday) {
          return true;
        }
        date = iterator.next({
          skipToDate: date
            .add(getDifferenceBetweenWeekdays(date.get('weekday'), args.weekday), 'day')
            .granularity('day'),
        }).value;
      }
      return false;
    }
    if (!args.date) {
      throw new ArgumentError(
        `"occursOn()" must be called with either a "date" or "weekday" argument`,
      );
    }
    if (this.hasDuration) {
      const maxDuration = this.getMaxDuration('occursOn', args);
      const iterator = this._run({
        start: args.date.subtract(maxDuration, 'millisecond'),
        end: args.date,
      });
      for (const date of iterator) {
        if (date.end.isBefore(args.date)) continue;
        if (date.isAfter(args.date)) return false;
        return true;
      }
      return false;
    }
    for (const day of this._run({ start: args.date, end: args.date })) {
      return !!day;
    }
    return false;
  }
  /**
   * Returns true if an occurrence starts after the provided datetime.
   * If the `excludeStart` option is provided, then occurrences
   * equal to the provided datetime are ignored.
   *
   * If the occurrence generator has a duration, and `excludeStart !== true`,
   * and a `maxDuration` argument is supplied (either in the constructor or
   * here), then any occurrence that's end time is after/equal to the provided
   * datetime return true.
   */
  occursAfter(date, options = {}) {
    const adapter = this.normalizeDateInput(date);
    if (this.hasDuration && !options.excludeStart) {
      const maxDuration = this.getMaxDuration('occursAfter', options);
      const iterator = this._run({
        start: adapter.subtract(maxDuration, 'millisecond'),
      });
      for (const date of iterator) {
        if (date.end.isBefore(adapter)) continue;
        return true;
      }
      return false;
    }
    for (const day of this._run({ start: adapter })) {
      if (options.excludeStart && day.isEqual(adapter)) {
        continue;
      }
      return true;
    }
    return false;
  }
  /**
   * Returns true if an occurrence starts before the provided datetime.
   * If the `excludeStart` option is provided, then occurrences
   * equal to the provided datetime are ignored.
   *
   * If the occurrence generator has a duration, and `excludeStart` is
   * also provided, then this will only return true if an occurrence
   * both starts and ends before the provided datetime.
   */
  occursBefore(date, options = {}) {
    const adapter = this.normalizeDateInput(date);
    if (this.hasDuration && options.excludeStart) {
      for (const day of this._run({ end: adapter, reverse: true })) {
        if (day.end.isAfterOrEqual(adapter)) continue;
        return true;
      }
      return false;
    }
    for (const day of this._run({ end: adapter, reverse: true })) {
      if (options.excludeStart && day.isEqual(adapter)) {
        continue;
      }
      return true;
    }
    return false;
  }
  get dateAdapter() {
    return DateAdapterBase$1.adapter;
  }
  normalizeOccurrencesArgs(rawArgs) {
    return Object.assign(Object.assign({}, rawArgs), {
      start: this.normalizeDateInput(rawArgs.start),
      end: this.normalizeDateInput(rawArgs.end),
    });
  }
  normalizeCollectionsArgs(rawArgs) {
    if (rawArgs.reverse !== undefined) {
      throw new ArgumentError(
        '`collections()` does not support the `reverse` option at this time.',
      );
    }
    return Object.assign(Object.assign({}, rawArgs), {
      start: this.normalizeDateInput(rawArgs.start),
      end: this.normalizeDateInput(rawArgs.end),
    });
  }
  normalizeOccursOnArgs(rawArgs = {}) {
    return Object.assign(Object.assign({}, rawArgs), {
      date: this.normalizeDateInput(rawArgs.date),
      after: this.normalizeDateInput(rawArgs.after),
      before: this.normalizeDateInput(rawArgs.before),
      excludeDates:
        rawArgs.excludeDates && rawArgs.excludeDates.map(date => this.normalizeDateInput(date)),
    });
  }
  normalizeRunArgs(args) {
    return Object.assign(Object.assign({}, args), {
      start: this.normalizeDateInput(args.start),
      end: this.normalizeDateInput(args.end),
    });
  }
  normalizeDateInput(date) {
    return date ? dateInputToDateTime(date, this.timezone) : undefined;
  }
  normalizeDateInputToAdapter(date) {
    if (!date) return;
    return dateInputToDateAdapter(date);
  }
  normalizeRunOutput(date) {
    return normalizeDateTimeTimezone$1(date, this.timezone);
  }
  getMaxDuration(method, options) {
    const maxDuration = options.maxDuration || this.maxDuration;
    if (!Number.isInteger(maxDuration)) {
      throw new ArgumentError(
        `When an occurrence generator ` +
          `has a duration, a 'maxDuration' argument must be supplied ` +
          `to ${method}().`,
      );
    }
    return maxDuration;
  }
}
class OccurrenceIterator {
  constructor(iterable, args) {
    this.iterable = iterable;
    this.args = args;
    // Need to assert the return type of these methods to prevent typescript from
    // incorrectly reducing them to `DateAdapterBase & { generators: G }`.
    this[Symbol.iterator] = () => this.occurrenceIterator();
    this.iterator = iterable._run(args);
    this.isInfinite = iterable.isInfinite;
  }
  next(args) {
    return this.occurrenceIterator(args).next();
  }
  toArray() {
    if (this.args.end || this.args.take || !this.isInfinite) {
      return Array.from(this.occurrenceIterator());
    }
    throw new InfiniteLoopError(
      'OccurrenceIterator#toArray() can only be called if the iterator ' +
        'is not infinite, or you provide and `end` argument, or you provide ' +
        'a `take` argument.',
    );
  }
  *occurrenceIterator(rawArgs) {
    let args = this.normalizeRunArgs(rawArgs);
    let date = this.iterator.next(args).value;
    while (date) {
      const yieldArgs = yield this.normalizeDateOutput(date);
      args = this.normalizeRunArgs(yieldArgs);
      date = this.iterator.next(args).value;
    }
    return undefined;
  }
  normalizeRunArgs(args) {
    return {
      skipToDate: this.normalizeDateInput(args && args.skipToDate),
    };
  }
  normalizeDateInput(date) {
    return date ? dateInputToDateTime(date, this.iterable.timezone) : undefined;
  }
  normalizeDateOutput(date) {
    if (!date) return;
    return date ? DateAdapterBase$1.adapter.fromDateTime(date) : undefined;
  }
}
class Collection {
  constructor(dates = [], granularity, periodStart, periodEnd) {
    this.dates = dates;
    this.granularity = granularity;
    this.periodStart = periodStart;
    this.periodEnd = periodEnd;
  }
}
class CollectionIterator {
  constructor(iterable, args) {
    this.iterable = iterable;
    this.args = args;
    this.granularity = 'year';
    this[Symbol.iterator] = () => this.iterator;
    if (args.granularity) {
      this.granularity = args.granularity;
      if (this.granularity === 'week' && !args.weekStart) {
        throw new Error('"week" granularity requires `weekStart` arg');
      }
    }
    if (args.weekStart) {
      this.weekStart = args.weekStart;
    }
    if (args.reverse) {
      throw new Error(
        '`OccurrenceGenerator#collections()` does not support iterating in reverse. ' +
          'Though `OccurrenceGenerator#occurrences()` does support iterating in reverse.',
      );
    }
    // Set the end arg, if present, to the end of the period.
    this.args = Object.assign(Object.assign({}, args), {
      start: args.start || iterable._run().next().value,
      end: args.end && this.getPeriod(args.end).end,
    });
    this.startDate =
      (this.args.start && this.normalizeDateOutput(this.getPeriod(this.args.start).start)) || null;
    this.iterator = this.collectionIterator();
  }
  next() {
    return this.iterator.next();
  }
  /**
   * While `next()` and `[Symbol.iterator]` both share state,
   * `toArray()` does not share state and always returns the whole
   * collections array.
   */
  toArray() {
    if (this.args.end || this.args.take || !this.iterable.isInfinite) {
      const collections = [];
      for (const collection of this.collectionIterator()) {
        collections.push(collection);
      }
      return collections;
    }
    throw new InfiniteLoopError(
      'CollectionIterator#toArray() can only be called if the iterator ' +
        'is not infinite, or you provide and `end` argument, or you provide ' +
        'a `take` argument.',
    );
  }
  normalizeDateOutput(date) {
    if (!date) return;
    return DateAdapterBase$1.adapter.fromDateTime(date);
  }
  *collectionIterator() {
    if (!this.startDate) return;
    let iterator = this.occurrenceIterator();
    let date = iterator.next().value;
    if (!date) return;
    // `period` === `periodStart` unless the granularity
    // is `MONTHLY` and a `weekStart` param was provided. In this case,
    // period holds a date === the first of the current month while
    // periodStart holds a date === the beginning of the first week of the month
    // (which might be in the the previous month). Read the
    // `OccurrenceGenerator#collections()` description for more info.
    let period = this.getPeriod(this.args.start);
    let dates = [];
    let index = 0;
    while (date && (this.args.take === undefined || this.args.take > index)) {
      while (date && date.isBeforeOrEqual(period.end)) {
        dates.push(date);
        date = iterator.next().value;
      }
      yield new Collection(
        dates.map(date => this.normalizeDateOutput(date)),
        this.granularity,
        this.normalizeDateOutput(period.start),
        this.normalizeDateOutput(period.end),
      );
      if (!date) return;
      dates = [];
      period = !this.args.skipEmptyPeriods
        ? this.getPeriod(this.incrementPeriod(period.period))
        : this.getPeriod(date);
      // With these args, periods may overlap and the same date may show up
      // in two periods. Because of this, we need to reset the iterator
      // (otherwise it won't return a date it has already returned).
      if (this.granularity === 'month' && this.weekStart) {
        iterator = this.iterable._run({
          start: period.start,
          end: this.args.end,
        });
        date = iterator.next().value;
      }
      index++;
    }
  }
  getPeriod(date) {
    let start;
    let end;
    let period;
    if (this.granularity === 'month' && this.weekStart) {
      start = date.granularity('month').granularity('week', { weekStart: this.weekStart });
      end = date.endGranularity('month').endGranularity('week', { weekStart: this.weekStart });
      period = date.granularity('month');
    } else {
      start = date.granularity(this.granularity, { weekStart: this.weekStart });
      end = date.endGranularity(this.granularity, { weekStart: this.weekStart });
      period = start;
    }
    return { start, end, period };
  }
  incrementPeriod(date) {
    return date.add(1, this.granularity);
  }
  occurrenceIterator() {
    let start = this.args.start || this.iterable._run().next().value;
    if (!start) return this.iterable._run(this.args);
    start = this.getPeriod(start).start;
    return this.iterable._run({
      start,
      end: this.args.end,
    });
  }
}
class Operator extends OccurrenceGenerator {
  constructor(streams, config) {
    super(config);
    this.streams = streams;
    this.config = config;
    this.timezone = config.timezone;
    this.streams = streams.map(stream =>
      stream instanceof Operator ? stream : stream.set('timezone', this.timezone),
    );
    this.isInfinite = this.calculateIsInfinite();
    this.hasDuration = this.calculateHasDuration();
  }
  normalizeDateInput(date) {
    if (!date) return;
    return dateInputToDateTime(date, this.timezone);
  }
  normalizeRunOutput(date) {
    return normalizeDateTimeTimezone$1(date, this.timezone);
  }
}

/**
 * Simple class that lets us access the `value`, `runArgs`, and `done`
 * status of an OccurrenceGenerator iterator
 */
class IterableWrapper {
  constructor(generator, runArgs) {
    this.runArgs = runArgs;
    this.stream = generator._run(this.runArgs);
    this.next();
  }
  next(args) {
    const { done, value } = this.stream.next(args);
    this.done = typeof done === 'boolean' ? done : true;
    this.value = value;
    return { done, value };
  }
}
function processYieldArgs(streams, options = {}, yieldArgs = {}) {
  if (!yieldArgs.skipToDate || streams.length === 0) return;
  // check for invalid `skipToDate` option
  if (
    options.reverse
      ? streams.every(s => s.value.isBeforeOrEqual(yieldArgs.skipToDate))
      : streams.every(s => s.value.isAfterOrEqual(yieldArgs.skipToDate))
  ) {
    throw new Error(
      'A provided `skipToDate` option must be greater than the last yielded date ' +
        '(or smaller, in the case of reverse iteration)',
    );
  }
  for (const stream of streams) {
    if (stream.done) continue; // no point in calling `next()`
    if (
      options.reverse
        ? stream.value.isBeforeOrEqual(yieldArgs.skipToDate)
        : stream.value.isAfterOrEqual(yieldArgs.skipToDate)
    ) {
      // This can happen there are two streams and one stream starts after the other finishes.
      // Or, when iterating in reverse, when one stream ends before the other starts.
      // In this case we don't want to call `next()` because it will throw an error.
      // In both of these cases, calling `next()` won't do anything anyway.
      continue;
    }
    stream.next(yieldArgs);
  }
}
/** sorts ascending with completed iterables at the end */
function streamsComparer(a, b) {
  if (a.done && b.done) return 0;
  if (a.done) return 1;
  if (b.done) return -1;
  if (a.value.isAfter(b.value)) return 1;
  return -1;
}
/** sorts descending with completed iterables at the start */
function streamsReverseComparer(a, b) {
  if (a.done && b.done) return 0;
  if (a.done) return -1;
  if (b.done) return 1;
  if (a.value.isAfter(b.value)) return -1;
  return 1;
}
function selectNextIterable(streams, options = {}, yieldArgs = {}) {
  processYieldArgs(streams, options, yieldArgs);
  return streams
    .sort(options.reverse ? streamsReverseComparer : streamsComparer)
    .filter(s => !s.done)
    .shift();
}
function selectLastIterable(streams, options = {}, yieldArgs = {}) {
  processYieldArgs(streams, options, yieldArgs);
  return streams
    .sort(options.reverse ? streamsReverseComparer : streamsComparer)
    .filter(s => !s.done)
    .pop();
}

/**
 * An operator function which accepts a spread of occurrence generators
 * and adds their occurrences to the output.
 *
 * @param streams a spread of occurrence generators
 */
function add(...streams) {
  return options => new AddOperator(streams, options);
}
class AddOperator extends Operator {
  set(_, value) {
    return new AddOperator(
      this.streams.map(stream => stream.set('timezone', value)),
      Object.assign(Object.assign({}, this.config), {
        base: this.config.base && this.config.base.set('timezone', value),
        timezone: value,
      }),
    );
  }
  *_run(args = {}) {
    const wrappedStreams = this.streams.map(input => new IterableWrapper(input, args));
    if (this.config.base) {
      wrappedStreams.push(new IterableWrapper(this.config.base, args));
    }
    if (wrappedStreams.length === 0) return;
    let stream = selectNextIterable(wrappedStreams, args);
    while (stream && !stream.done) {
      // yield the current stream's value
      const yieldArgs = yield this.normalizeRunOutput(stream.value);
      if (!(yieldArgs && yieldArgs.skipToDate)) {
        // iterate the current stream
        stream.next();
      }
      // select the next stream
      stream = selectNextIterable(wrappedStreams, args, yieldArgs);
    }
  }
  calculateIsInfinite() {
    return (
      (this.config.base && this.config.base.isInfinite) ||
      this.streams.some(stream => stream.isInfinite)
    );
  }
  calculateHasDuration() {
    const streamsDuration = this.streams.every(stream => stream.hasDuration);
    if (!this.config.base) return streamsDuration;
    return this.config.base.hasDuration && streamsDuration;
  }
}

class Calendar extends OccurrenceGenerator {
  /**
   *
   * Create a new Calendar object with the specified options.
   *
   * ### Options
   *
   * - **timezone**: The timezone that yielded occurrences should be *displayed* in.
   *   Note, this one affects the *displayed* timezone of yielded occurrences.
   *   For rules, occurrences are first found using the unmodified rule
   *   config (including whatever timezone the `start` datetime is defined
   *   in), and then converted to the timezone specified here before being
   *   yielded. By default, the timezone is *local* time (`null`). So if you don't
   *   want your rules to be displayed in local time, you must supply a
   *   timezone argument.
   * - **data**: arbitrary data you can associate with this Calendar. This
   *   is the only mutable property of `Calendar` objects.
   * - **maxDuration**: currently unused.
   * - **schedules**: either an occurrence generator, or an array of occurrence generators, which should
   *   be used to build up this Calendar's occurrences. The Calendar will display the union of occurrences
   *   its schedules produce.
   *
   */
  constructor(args = {}) {
    super(args);
    this.schedules = [];
    this.data = args.data;
    if (args.schedules) {
      this.schedules = Array.isArray(args.schedules) ? args.schedules : [args.schedules];
      this.schedules = this.schedules.map(schedule => schedule.set('timezone', this.timezone));
    }
    this.isInfinite = this.schedules.some(schedule => schedule.isInfinite);
    this.hasDuration = this.schedules.every(schedule => schedule.hasDuration);
  }
  occurrences(args = {}) {
    return new OccurrenceIterator(this, this.normalizeOccurrencesArgs(args));
  }
  collections(args = {}) {
    return new CollectionIterator(this, this.normalizeCollectionsArgs(args));
  }
  set(prop, value, options) {
    if (prop === 'timezone') {
      return new Calendar({
        schedules: this.schedules.map(schedule => schedule.set(prop, value, options)),
        data: this.data,
        timezone: value,
        maxDuration: this.maxDuration,
      });
    } else if (prop === 'schedules') {
      return new Calendar({
        schedules: Array.isArray(value) ? value : [value],
        data: this.data,
        timezone: this.timezone,
        maxDuration: this.maxDuration,
      });
    }
    throw new ArgumentError('Unknown value for `prop`: ' + `"${prop}"`);
  }
  *_run(args = {}) {
    if (this.schedules.length === 0) return;
    const count = args.take;
    delete args.take;
    let iterator;
    if (this.schedules.length === 1) {
      iterator = this.schedules[0]._run(args);
    } else {
      iterator = new AddOperator(this.schedules, {
        timezone: this.timezone,
      })._run(args);
    }
    let date = iterator.next().value;
    let index = 0;
    while (date && (count === undefined || count > index)) {
      date = date.add(this, 'generator');
      const yieldArgs = yield this.normalizeRunOutput(date);
      date = iterator.next(yieldArgs).value;
      index++;
    }
    return undefined;
  }
}

class Dates extends OccurrenceGenerator {
  constructor(args = {}) {
    super(args);
    this.adapters = [];
    this.isInfinite = false;
    this.maxDuration = 0;
    this.datetimes = [];
    this.data = args.data;
    if (args.dates) {
      this.adapters = args.dates.map(date => {
        let adapter = this.normalizeDateInputToAdapter(date);
        if (args.duration && !adapter.duration) {
          adapter = adapter.set('duration', args.duration);
        }
        return adapter.set('timezone', this.timezone);
      });
      this.datetimes = this.adapters.map(adapter => adapter.toDateTime());
    }
    this.hasDuration = this.datetimes.every(date => !!date.duration);
    if (this.hasDuration) {
      this.maxDuration = this.adapters.reduce(
        (prev, curr) => (curr.duration > prev ? curr.duration : prev),
        0,
      );
    }
  }
  get length() {
    return this.adapters.length;
  }
  /** Returns the first occurrence or, if there are no occurrences, null. */
  get firstDate() {
    return this.adapters[0] || null;
  }
  /** Returns the last occurrence or, if there are no occurrences, null. */
  get lastDate() {
    return this.adapters[this.length - 1] || null;
  }
  occurrences(args = {}) {
    return new OccurrenceIterator(this, this.normalizeOccurrencesArgs(args));
  }
  collections(args = {}) {
    return new CollectionIterator(this, this.normalizeCollectionsArgs(args));
  }
  add(value) {
    return new Dates({
      dates: [...this.adapters, value],
      timezone: this.timezone,
      data: this.data,
    });
  }
  remove(value) {
    const dates = this.adapters.slice();
    const input = this.normalizeDateInputToAdapter(value);
    const index = dates.findIndex(date => date.valueOf() === input.valueOf());
    if (index >= 0) {
      dates.splice(index, 1);
    }
    return new Dates({
      dates,
      timezone: this.timezone,
      data: this.data,
    });
  }
  set(prop, value, options = {}) {
    let timezone = this.timezone;
    let dates = this.adapters.slice();
    if (prop === 'timezone') {
      if (value === this.timezone) return this;
      else if (options.keepLocalTime) {
        dates = this.adapters.map(adapter => {
          const json = adapter.toJSON();
          json.timezone = value;
          return this.dateAdapter.fromJSON(json);
        });
      }
      timezone = value;
    } else if (prop === 'dates') {
      dates = value;
    } else if (prop === 'duration') {
      dates = dates.map(date => date.set('duration', value || 0));
    } else {
      throw new ArgumentError(
        `Unexpected prop argument "${prop}". Accepted values are "timezone" or "dates"`,
      );
    }
    return new Dates({
      dates,
      data: this.data,
      timezone,
    });
  }
  filter(fn) {
    return new Dates({
      dates: this.adapters.filter(fn),
      data: this.data,
      timezone: this.timezone,
    });
  }
  *_run(args = {}) {
    let dates = this.datetimes.sort(dateTimeSortComparer);
    if (args.start) {
      dates = dates.filter(date => date.isAfterOrEqual(args.start));
    }
    if (args.end) {
      dates = dates.filter(date => date.isBeforeOrEqual(args.end));
    }
    if (args.reverse) {
      dates = dates.slice().reverse();
    }
    if (args.take) {
      dates = dates.slice(0, args.take);
    }
    const dateCache = dates.slice();
    let date = dateCache.shift();
    let yieldArgs;
    while (date) {
      if (
        yieldArgs &&
        yieldArgs.skipToDate &&
        (args.reverse ? yieldArgs.skipToDate.isBefore(date) : yieldArgs.skipToDate.isAfter(date))
      ) {
        date = dateCache.shift();
        continue;
      }
      date = date.add(this, 'generator');
      yieldArgs = yield this.normalizeRunOutput(date);
      // Here, we are matching the behavior of the RecurrenceRulesIterator
      if (
        yieldArgs &&
        yieldArgs.skipToDate &&
        (args.reverse
          ? yieldArgs.skipToDate.isAfterOrEqual(date)
          : yieldArgs.skipToDate.isBeforeOrEqual(date))
      ) {
        throw new Error(
          'A provided `skipToDate` option must be greater than the last yielded date ' +
            '(or smaller, in the case of reverse iteration)',
        );
      }
      date = dateCache.shift();
    }
    return undefined;
  }
}

class InvalidDateAdapterError extends Error {}
let dateAdapterConfig;
class DateAdapterBase {
  constructor(_date, options) {
    /**
     * The metadata property is intended to store arbitrary data
     * related to this DateAdapter. Unlike other properties, the
     * metadata property is intended to be mutable.
     */
    this.metadata = {};
    this.duration = (options && options.duration) || 0;
    this.generators = (options && options.generators && options.generators.slice()) || [];
    if (!Number.isInteger(this.duration) || this.duration < 0) {
      throw new InvalidDateAdapterError('duration must be a non-negative integer');
    }
  }
  static set adapter(value) {
    if (dateAdapterConfig) {
      throw new Error(`"${dateAdapterConfig.name}" has already been configured.`);
    }
    dateAdapterConfig = value;
  }
  static get adapter() {
    if (!dateAdapterConfig) {
      throw new Error('No date adapter has been configured. See rSchedule docs.');
    }
    return dateAdapterConfig;
  }
  static isDate(_object) {
    throw unimplementedError('isDate()');
  }
  static fromDate(_date, _options) {
    throw unimplementedError('fromDate()');
  }
  static fromJSON(_json) {
    throw unimplementedError('fromJSON()');
  }
  static fromDateTime(_datetime) {
    throw unimplementedError('fromDateTime()');
  }
  /**
   * Returns a string in simplified extended ISO format (ISO 8601).
   *
   * _Note: this method is intended for testing and its
   * implementation isn't particularly performant._
   */
  toISOString() {
    return this.set('timezone', 'UTC')
      .toDateTime()
      .toISOString();
  }
  toDateTime() {
    const date = DateTime.fromJSON(
      Object.assign(Object.assign({}, this.toJSON()), {
        generators: this.generators,
        metadata: this.metadata,
      }),
    );
    return date;
  }
}
DateAdapterBase.hasTimezoneSupport = false;
function unimplementedError(name) {
  return new Error(`You must implement the "${name}" method for this DateAdapter class`);
}
var DateAdapter;
(function(DateAdapter) {
  DateAdapter.WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
  DateAdapter.MILLISECONDS_IN_SECOND = 1000;
  DateAdapter.MILLISECONDS_IN_MINUTE = DateAdapter.MILLISECONDS_IN_SECOND * 60;
  DateAdapter.MILLISECONDS_IN_HOUR = DateAdapter.MILLISECONDS_IN_MINUTE * 60;
  DateAdapter.MILLISECONDS_IN_DAY = DateAdapter.MILLISECONDS_IN_HOUR * 24;
  DateAdapter.MILLISECONDS_IN_WEEK = DateAdapter.MILLISECONDS_IN_DAY * 7;
})(DateAdapter || (DateAdapter = {}));
class InvalidDateTimeError extends Error {}
class DateTime {
  constructor(date, timezone, duration, generators, options = {}) {
    this.metadata = {};
    this.date = new Date(date);
    this.timezone = timezone || null;
    this.duration = duration || 0;
    this.generators = (generators && generators.slice()) || [];
    if (!Number.isInteger(this.duration) || this.duration < 0) {
      throw new InvalidDateTimeError('duration must be a non-negative integer');
    }
    if (options.metadata) {
      Object.assign(this.metadata, options.metadata);
    }
    this.assertIsValid();
  }
  // /**
  //  * Similar to `Array.isArray()`, `isInstance()` provides a surefire method
  //  * of determining if an object is a `DateTime` by checking against the
  //  * global symbol registry.
  //  */
  // static isInstance(object: any): object is DateTime {
  //   return !!(object && object[DATETIME_ID]);
  // }
  static fromJSON(json) {
    const date = new Date(
      Date.UTC(
        json.year,
        json.month - 1,
        json.day,
        json.hour,
        json.minute,
        json.second,
        json.millisecond,
      ),
    );
    return new DateTime(date, json.timezone, json.duration, json.generators, {
      metadata: json.metadata,
    });
  }
  static fromDateAdapter(adapter) {
    return DateTime.fromJSON(
      Object.assign(Object.assign({}, adapter.toJSON()), {
        generators: adapter.generators,
        metadata: adapter.metadata,
      }),
    );
  }
  /**
   * Returns `undefined` if `duration` is `0`. Else returns
   * the `end` date.
   */
  get end() {
    if (!this.duration) return;
    if (this._end) return this._end;
    this._end = this.add(this.duration, 'millisecond');
    return this._end;
  }
  // While we constrain the argument to be another DateAdapter in typescript
  // we handle the case of someone passing in another type of object in javascript
  isEqual(object) {
    if (!object) {
      return false;
    }
    assertSameTimeZone(this, object);
    return this.valueOf() === object.valueOf();
  }
  isBefore(object) {
    assertSameTimeZone(this, object);
    return this.valueOf() < object.valueOf();
  }
  isBeforeOrEqual(object) {
    assertSameTimeZone(this, object);
    return this.valueOf() <= object.valueOf();
  }
  isAfter(object) {
    assertSameTimeZone(this, object);
    return this.valueOf() > object.valueOf();
  }
  isAfterOrEqual(object) {
    assertSameTimeZone(this, object);
    return this.valueOf() >= object.valueOf();
  }
  isOccurring(object) {
    if (!this.duration) {
      throw new Error('DateTime#isOccurring() is only applicable to DateTimes with durations');
    }
    assertSameTimeZone(this, object);
    return (
      object.isAfterOrEqual(this) && object.isBeforeOrEqual(this.add(this.duration, 'millisecond'))
    );
  }
  add(amount, unit) {
    switch (unit) {
      case 'generator': {
        const generators = this.generators.slice();
        generators.unshift(amount);
        return new DateTime(this.date, this.timezone, this.duration, generators);
      }
      case 'year':
        return this.forkDateTime(addUTCYears(this.date, amount));
      case 'month':
        return this.forkDateTime(addUTCMonths(this.date, amount));
      case 'week':
        return this.forkDateTime(addUTCWeeks(this.date, amount));
      case 'day':
        return this.forkDateTime(addUTCDays(this.date, amount));
      case 'hour':
        return this.forkDateTime(addUTCHours(this.date, amount));
      case 'minute':
        return this.forkDateTime(addUTCMinutes(this.date, amount));
      case 'second':
        return this.forkDateTime(addUTCSeconds(this.date, amount));
      case 'millisecond':
        return this.forkDateTime(addUTCMilliseconds(this.date, amount));
      default:
        throw new Error('Invalid unit provided to `DateTime#add`');
    }
  }
  subtract(amount, unit) {
    switch (unit) {
      case 'year':
        return this.forkDateTime(subUTCYears(this.date, amount));
      case 'month':
        return this.forkDateTime(subUTCMonths(this.date, amount));
      case 'week':
        return this.forkDateTime(subUTCWeeks(this.date, amount));
      case 'day':
        return this.forkDateTime(subUTCDays(this.date, amount));
      case 'hour':
        return this.forkDateTime(subUTCHours(this.date, amount));
      case 'minute':
        return this.forkDateTime(subUTCMinutes(this.date, amount));
      case 'second':
        return this.forkDateTime(subUTCSeconds(this.date, amount));
      case 'millisecond':
        return this.forkDateTime(subUTCMilliseconds(this.date, amount));
      default:
        throw new Error('Invalid unit provided to `DateTime#subtract`');
    }
  }
  get(unit) {
    switch (unit) {
      case 'year':
        return this.date.getUTCFullYear();
      case 'month':
        return this.date.getUTCMonth() + 1;
      case 'yearday':
        return getUTCYearDay(this.date);
      case 'weekday':
        return DateAdapter.WEEKDAYS[this.date.getUTCDay()];
      case 'day':
        return this.date.getUTCDate();
      case 'hour':
        return this.date.getUTCHours();
      case 'minute':
        return this.date.getUTCMinutes();
      case 'second':
        return this.date.getUTCSeconds();
      case 'millisecond':
        return this.date.getUTCMilliseconds();
      default:
        throw new Error('Invalid unit provided to `DateTime#set`');
    }
  }
  set(unit, value) {
    if (unit === 'duration') {
      return new DateTime(this.date, this.timezone, value, this.generators);
    }
    if (unit === 'generators') {
      return new DateTime(this.date, this.timezone, this.duration, value);
    }
    let date = new Date(this.date);
    switch (unit) {
      case 'year':
        date.setUTCFullYear(value);
        break;
      case 'month': {
        // If the current day of the month
        // is greater than days in the month we are moving to, we need to also
        // set the day to the end of that month.
        const length = monthLength(value, date.getUTCFullYear());
        const day = date.getUTCDate();
        if (day > length) {
          date.setUTCDate(1);
          date.setUTCMonth(value);
          date = subUTCDays(date, 1);
        } else {
          date.setUTCMonth(value - 1);
        }
        break;
      }
      case 'day':
        date.setUTCDate(value);
        break;
      case 'hour':
        date.setUTCHours(value);
        break;
      case 'minute':
        date.setUTCMinutes(value);
        break;
      case 'second':
        date.setUTCSeconds(value);
        break;
      case 'millisecond':
        date.setUTCMilliseconds(value);
        break;
      default:
        throw new Error('Invalid unit provided to `DateTime#set`');
    }
    return this.forkDateTime(date);
  }
  granularity(granularity, opt = {}) {
    let date = this.forkDateTime(this.date);
    switch (granularity) {
      case 'year':
        date = date.set('month', 1);
      case 'month':
        date = date.set('day', 1);
        break;
      case 'week':
        date = setDateToStartOfWeek(date, opt.weekStart);
    }
    switch (granularity) {
      case 'year':
      case 'month':
      case 'week':
      case 'day':
        date = date.set('hour', 0);
      case 'hour':
        date = date.set('minute', 0);
      case 'minute':
        date = date.set('second', 0);
      case 'second':
        date = date.set('millisecond', 0);
      case 'millisecond':
        return date;
      default:
        throw new Error('Invalid granularity provided to `DateTime#granularity`: ' + granularity);
    }
  }
  endGranularity(granularity, opt = {}) {
    let date = this.forkDateTime(this.date);
    switch (granularity) {
      case 'year':
        date = date.set('month', 12);
      case 'month':
        date = date.set('day', monthLength(date.get('month'), date.get('year')));
        break;
      case 'week':
        date = setDateToEndOfWeek(date, opt.weekStart);
    }
    switch (granularity) {
      case 'year':
      case 'month':
      case 'week':
      case 'day':
        date = date.set('hour', 23);
      case 'hour':
        date = date.set('minute', 59);
      case 'minute':
        date = date.set('second', 59);
      case 'second':
        date = date.set('millisecond', 999);
      case 'millisecond':
        return date;
      default:
        throw new Error('Invalid granularity provided to `DateTime#granularity`: ' + granularity);
    }
  }
  toISOString() {
    return this.date.toISOString();
  }
  toDateTime() {
    return this;
  }
  toJSON() {
    const json = {
      timezone: this.timezone,
      year: this.get('year'),
      month: this.get('month'),
      day: this.get('day'),
      hour: this.get('hour'),
      minute: this.get('minute'),
      second: this.get('second'),
      millisecond: this.get('millisecond'),
    };
    if (this.duration) {
      json.duration = this.duration;
    }
    return json;
  }
  valueOf() {
    return this.date.valueOf();
  }
  assertIsValid() {
    if (isNaN(this.valueOf())) {
      throw new InvalidDateTimeError('DateTime has invalid date.');
    }
    return true;
  }
  forkDateTime(date) {
    return new DateTime(date, this.timezone, this.duration, this.generators);
  }
}
function assertSameTimeZone(x, y) {
  if (x.timezone !== y.timezone) {
    throw new InvalidDateTimeError(
      'Attempted to compare a datetime to another date in a different timezone: ' +
        JSON.stringify(x) +
        ' and ' +
        JSON.stringify(y),
    );
  }
  return true;
}
function setDateToStartOfWeek(date, wkst) {
  const index = orderedWeekdays(wkst).indexOf(date.get('weekday'));
  return date.subtract(index, 'day');
}
function setDateToEndOfWeek(date, wkst) {
  const index = orderedWeekdays(wkst).indexOf(date.get('weekday'));
  return date.add(6 - index, 'day');
}
function orderedWeekdays(wkst = 'SU') {
  const wkdays = DateAdapter.WEEKDAYS.slice();
  let index = wkdays.indexOf(wkst);
  while (index !== 0) {
    shiftArray(wkdays);
    index--;
  }
  return wkdays;
}
function shiftArray(array, from = 'first') {
  if (array.length === 0) {
    return array;
  } else if (from === 'first') {
    array.push(array.shift());
  } else {
    array.unshift(array.pop());
  }
  return array;
}
/**
 * Returns the days in the given month.
 *
 * @param month base-1
 * @param year
 */
function monthLength(month, year) {
  const block = {
    1: 31,
    2: getDaysInFebruary(year),
    3: 31,
    4: 30,
    5: 31,
    6: 30,
    7: 31,
    8: 31,
    9: 30,
    10: 31,
    11: 30,
    12: 31,
  };
  return block[month];
}
function getDaysInFebruary(year) {
  return isLeapYear(year) ? 29 : 28;
}
// taken from date-fn
function isLeapYear(year) {
  return year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0);
}
function getUTCYearDay(now) {
  const start = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
  const diff = now.valueOf() - start.valueOf();
  return 1 + Math.floor(diff / DateAdapter.MILLISECONDS_IN_DAY);
}
/**
 * These functions are basically lifted from `date-fns`, but changed
 * to use the UTC date methods, which `date-fns` doesn't support.
 */
function toInteger(input) {
  if (input === null || input === true || input === false) {
    return NaN;
  }
  const int = Number(input);
  if (isNaN(int)) {
    return int;
  }
  return int < 0 ? Math.ceil(int) : Math.floor(int);
}
function addMilliseconds(dirtyDate, dirtyAmount) {
  if (arguments.length < 2) {
    throw new TypeError('2 arguments required, but only ' + arguments.length + ' present');
  }
  const timestamp = dirtyDate.valueOf();
  const amount = toInteger(dirtyAmount);
  return new Date(timestamp + amount);
}
function addUTCYears(date, input) {
  const amount = toInteger(input);
  return addUTCMonths(date, amount * 12);
}
function addUTCMonths(date, input) {
  const amount = toInteger(input);
  date = new Date(date);
  const desiredMonth = date.getUTCMonth() + amount;
  const dateWithDesiredMonth = new Date(0);
  dateWithDesiredMonth.setUTCFullYear(date.getUTCFullYear(), desiredMonth, 1);
  dateWithDesiredMonth.setUTCHours(0, 0, 0, 0);
  const daysInMonth = monthLength(
    dateWithDesiredMonth.getUTCMonth() + 1,
    dateWithDesiredMonth.getUTCFullYear(),
  );
  // Set the last day of the new month
  // if the original date was the last day of the longer month
  date.setUTCMonth(desiredMonth, Math.min(daysInMonth, date.getUTCDate()));
  return date;
}
function addUTCWeeks(date, input) {
  const amount = toInteger(input);
  const days = amount * 7;
  return addUTCDays(date, days);
}
function addUTCDays(date, input) {
  // by adding milliseconds rather than days, we supress the native Date object's automatic
  // daylight savings time conversions which we don't want in UTC mode
  return addUTCMilliseconds(date, toInteger(input) * DateAdapter.MILLISECONDS_IN_DAY);
}
function addUTCHours(date, input) {
  const amount = toInteger(input);
  return addMilliseconds(date, amount * DateAdapter.MILLISECONDS_IN_HOUR);
}
function addUTCMinutes(date, input) {
  const amount = toInteger(input);
  return addMilliseconds(date, amount * DateAdapter.MILLISECONDS_IN_MINUTE);
}
function addUTCSeconds(date, input) {
  const amount = toInteger(input);
  return addMilliseconds(date, amount * DateAdapter.MILLISECONDS_IN_SECOND);
}
function addUTCMilliseconds(date, input) {
  const amount = toInteger(input);
  const timestamp = date.getTime();
  return new Date(timestamp + amount);
}
function subUTCYears(date, amount) {
  return addUTCYears(date, -amount);
}
function subUTCMonths(date, amount) {
  return addUTCMonths(date, -amount);
}
function subUTCWeeks(date, amount) {
  return addUTCWeeks(date, -amount);
}
function subUTCDays(date, amount) {
  return addUTCDays(date, -amount);
}
function subUTCHours(date, amount) {
  return addUTCHours(date, -amount);
}
function subUTCMinutes(date, amount) {
  return addUTCMinutes(date, -amount);
}
function subUTCSeconds(date, amount) {
  return addUTCSeconds(date, -amount);
}
function subUTCMilliseconds(date, amount) {
  return addUTCMilliseconds(date, -amount);
}

// export function normalizeDateInput<T extends DateAdapter>(
//   dateAdapter: DateAdapterConstructor<T>,
//   input: DateInput<T>,
//   timezone: string | null,
// ): DateTime {
//   if (input instanceof DateTime) {
//     if (input.timezone !== timezone) {
//       return dateAdapter
//         .fromDateTime(input)
//         .set('timezone', timezone)
//         .toDateTime();
//     }
//     return input;
//   }
//   return input instanceof DateAdapter
//     ? input.set('timezone', timezone).toDateTime()
//     : new dateAdapter(input).toDateTime();
// }
function normalizeDateTimeTimezone(date, timezone) {
  if (date.timezone !== timezone) {
    return DateAdapterBase.adapter
      .fromDateTime(date)
      .set('timezone', timezone)
      .toDateTime();
  }
  return date;
}

class RuleBase extends OccurrenceGenerator {
  constructor(recurrenceRules, config, options = {}) {
    super(options);
    this.recurrenceRules = recurrenceRules;
    this.options = cloneRuleOptions(config);
    this.normOptions = normalizeRuleOptions(this.recurrenceRules, this.options);
    this.timezone =
      options.timezone !== undefined ? options.timezone : this.normOptions.start.timezone;
    this.data = options.data;
    this.hasDuration = !!config.duration;
    if (this.hasDuration) this.duration = config.duration;
    this.isInfinite = this.normOptions.end === undefined && this.normOptions.count === undefined;
  }
  occurrences(args = {}) {
    return new OccurrenceIterator(this, this.normalizeOccurrencesArgs(args));
  }
  collections(args = {}) {
    return new CollectionIterator(this, this.normalizeCollectionsArgs(args));
  }
  *_run(rawArgs = {}) {
    const args = this.normalizeRunArgs(rawArgs);
    const iterator = new RecurrenceRulesIterator(
      recurrenceRulesReducer(this.recurrenceRules),
      this.normOptions,
      args,
    );
    let date = iterator.next().value;
    let index = 0;
    while (date && (args.take === undefined || index < args.take)) {
      index++;
      date = date.add(this, 'generator');
      const yieldArgs = yield this.normalizeRunOutput(date);
      if (yieldArgs === null || yieldArgs === void 0 ? void 0 : yieldArgs.skipToDate) {
        // The RecurrenceRuleIterator might have a different timezone from the rule.
        // Because of this, the yieldArgs will not properly be normalized by the
        // OccurrenceIterator, so we need to do it here
        date = iterator.next(
          Object.assign(Object.assign({}, yieldArgs), {
            skipToDate: normalizeDateTimeTimezone(yieldArgs.skipToDate, iterator.start.timezone),
          }),
        ).value;
      } else {
        // theoretically, the yieldArgs are undefined here
        // maybe in the future there will be other yieldArg options though
        date = iterator.next(yieldArgs).value;
      }
    }
    return undefined;
  }
}

class Rule extends RuleBase {
  /**
   * Create a new Rule object with the specified rule config and options.
   *
   * ### Options
   *
   * - **timezone**: the timezone that yielded occurrences should be in. Note,
   *   this does not change the rule config. Occurrences are first found using
   *   the unmodified rule config, and then converted to the timezone specified
   *   here before being yielded.
   * - **data**: arbitrary data you can associate with this rule. This
   *   is the only mutable property of `Rule` objects.
   *
   * ### Rule Config
   *
   * - #### frequency
   *
   *   The frequency rule part identifies the type of recurrence rule. Valid values
   *   include `"SECONDLY"`, `"MINUTELY"`, `"HOURLY"`, `"DAILY"`, `"WEEKLY"`,
   *   `"MONTHLY"`, or `"YEARLY"`.
   *
   * - #### start
   *
   *   The start of the rule (not necessarily the first occurrence).
   *   Either a `DateAdapter` instance, date object, or `DateTime` object.
   *   The type of date object depends on the `DateAdapter` class used for this
   *   `Rule`.
   *
   * - #### end?
   *
   *   The end of the rule (not necessarily the last occurrence).
   *   Either a `DateAdapter` instance, date object, or `DateTime` object.
   *   The type of date object depends on the `DateAdapter` class used for this
   *   `Rule`.
   *
   * - #### duration?
   *
   *   A length of time expressed in milliseconds.
   *
   * - #### interval?
   *
   *   The interval rule part contains a positive integer representing at
   *   which intervals the recurrence rule repeats. The default value is
   *   `1`, meaning every second for a SECONDLY rule, every minute for a
   *   MINUTELY rule, every hour for an HOURLY rule, every day for a
   *   DAILY rule, every week for a WEEKLY rule, every month for a
   *   MONTHLY rule, and every year for a YEARLY rule. For example,
   *   within a DAILY rule, a value of `8` means every eight days.
   *
   * - #### count?
   *
   *   The count rule part defines the number of occurrences at which to
   *   range-bound the recurrence. `count` and `end` are both two different
   *   ways of specifying how a recurrence completes.
   *
   * - #### weekStart?
   *
   *   The weekStart rule part specifies the day on which the workweek starts.
   *   Valid values are `"MO"`, `"TU"`, `"WE"`, `"TH"`, `"FR"`, `"SA"`, and `"SU"`.
   *   This is significant when a WEEKLY rule has an interval greater than 1,
   *   and a `byDayOfWeek` rule part is specified. The
   *   default value is `"MO"`.
   *
   * - #### bySecondOfMinute?
   *
   *   The bySecondOfMinute rule part expects an array of seconds
   *   within a minute. Valid values are 0 to 60.
   *
   * - #### byMinuteOfHour?
   *
   *   The byMinuteOfHour rule part expects an array of minutes within an hour.
   *   Valid values are 0 to 59.
   *
   * - #### byHourOfDay?
   *
   *   The byHourOfDay rule part expects an array of hours of the day.
   *   Valid values are 0 to 23.
   *
   * - #### byDayOfWeek?
   *
   *   *note: the byDayOfWeek rule part is kinda complex. Blame the ICAL spec.*
   *
   *   The byDayOfWeek rule part expects an array. Each array entry can
   *   be a day of the week (`"SU"`, `"MO"` , `"TU"`, `"WE"`, `"TH"`,
   *   `"FR"`, `"SA"`). If the rule's `frequency` is either MONTHLY or YEARLY,
   *   Any entry can also be a tuple where the first value of the tuple is a
   *   day of the week and the second value is an positive/negative integer
   *   (e.g. `["SU", 1]`). In this case, the number indicates the nth occurrence of
   *   the specified day within the MONTHLY or YEARLY rule.
   *
   *   The behavior of byDayOfWeek changes depending on the `frequency`
   *   of the rule.
   *
   *   Within a MONTHLY rule, `["MO", 1]` represents the first Monday
   *   within the month, whereas `["MO", -1]` represents the last Monday
   *   of the month.
   *
   *   Within a YEARLY rule, the numeric value in a byDayOfWeek tuple entry
   *   corresponds to an offset within the month when the byMonthOfYear rule part is
   *   present, and corresponds to an offset within the year otherwise.
   *
   *   Regardless of rule `frequency`, if a byDayOfWeek entry is a string
   *   (rather than a tuple), it means "all of these days" within the specified
   *   frequency (e.g. within a MONTHLY rule, `"MO"` represents all Mondays within
   *   the month).
   *
   * - #### byDayOfMonth?
   *
   *   The byDayOfMonth rule part expects an array of days
   *   of the month. Valid values are 1 to 31 or -31 to -1.
   *
   *   For example, -10 represents the tenth to the last day of the month.
   *   The byDayOfMonth rule part *must not* be specified when the rule's
   *   `frequency` is set to WEEKLY.
   *
   * - #### byMonthOfYear?
   *
   *   The byMonthOfYear rule part expects an array of months
   *   of the year. Valid values are 1 to 12.
   *
   */
  constructor(config, options = {}) {
    super(Rule.recurrenceRules, config, options);
  }
  set(prop, value, tzoptions = {}) {
    let options = cloneRuleOptions(this.options);
    let timezone = this.timezone;
    if (prop === 'timezone') {
      if (value === this.timezone && !tzoptions.keepLocalTime) return this;
      else if (tzoptions.keepLocalTime) {
        const json = this.normalizeDateInput(options.start).toJSON();
        json.timezone = value;
        const adapter = this.dateAdapter.fromJSON(json);
        // prettier-ignore
        options.start =
                    options.start instanceof this.dateAdapter ? adapter :
                        options.start instanceof DateTime$1 ? adapter.toDateTime() :
                            adapter.date;
      }
      timezone = value;
    } else if (prop === 'options') {
      options = value;
    } else {
      options[prop] = value;
    }
    return new Rule(options, {
      data: this.data,
      maxDuration: this.maxDuration,
      timezone,
    });
  }
}
Rule.recurrenceRules = [];

class ScheduleBase extends OccurrenceGenerator {
  /**
   * Create a new Schedule object with the specified options.
   *
   * The order of precidence for rrules, rdates, exrules, and exdates is:
   *
   * 1. rrules are included
   * 2. exrules are excluded
   * 3. rdates are included
   * 4. exdates are excluded
   *
   */
  constructor(options) {
    super(options);
    this.data = options.data;
  }
  occurrences(args = {}) {
    return new OccurrenceIterator(this, this.normalizeOccurrencesArgs(args));
  }
  collections(args = {}) {
    return new CollectionIterator(this, this.normalizeCollectionsArgs(args));
  }
  *_run(args = {}) {
    const count = args.take;
    delete args.take;
    const iterator = this.occurrenceStream._run(args);
    let date = iterator.next().value;
    let index = 0;
    while (date && (count === undefined || count > index)) {
      date = date.add(this, 'generator');
      const yieldArgs = yield this.normalizeRunOutput(date);
      date = iterator.next(yieldArgs).value;
      index++;
    }
    return undefined;
  }
}

/**
 * An operator function which accepts a spread of occurrence generators
 * and removes their occurrences from the output.
 *
 * @param streams a spread of occurrence generators
 */
function subtract(...streams) {
  return options => new SubtractOperator(streams, options);
}
class SubtractOperator extends Operator {
  /** Not actually used but necessary for IRunnable interface */
  set(_, value) {
    return new SubtractOperator(
      this.streams.map(stream => stream.set('timezone', value)),
      Object.assign(Object.assign({}, this.config), {
        base: this.config.base && this.config.base.set('timezone', value),
        timezone: value,
      }),
    );
  }
  *_run(args = {}) {
    if (!this.config.base) return;
    const inclusion = new IterableWrapper(this.config.base, args);
    const exclusion = new IterableWrapper(
      new AddOperator(this.streams, {
        timezone: this.config.timezone,
      }),
      args,
    );
    cycleStreams(inclusion, exclusion, args);
    while (!inclusion.done) {
      const yieldArgs = yield this.normalizeRunOutput(inclusion.value);
      if (!(yieldArgs && yieldArgs.skipToDate)) {
        inclusion.next();
      }
      cycleStreams(inclusion, exclusion, args, yieldArgs);
    }
  }
  calculateIsInfinite() {
    return !!(this.config.base && this.config.base.isInfinite);
  }
  calculateHasDuration() {
    return !!(this.config.base && this.config.base.hasDuration);
  }
}
function cycleStreams(inclusion, exclusion, options = {}, yieldArgs = {}) {
  processYieldArgs([inclusion, exclusion], options, yieldArgs);
  iterateExclusion(inclusion, exclusion, options);
  while (!inclusion.done && !exclusion.done && inclusion.value.isEqual(exclusion.value)) {
    inclusion.next();
    iterateExclusion(inclusion, exclusion, options);
  }
}
function iterateExclusion(inclusion, exclusion, options = {}) {
  if (options.reverse) {
    while (!exclusion.done && !inclusion.done && exclusion.value.isAfter(inclusion.value)) {
      exclusion.next();
    }
    return;
  }
  while (!exclusion.done && !inclusion.done && exclusion.value.isBefore(inclusion.value)) {
    exclusion.next();
  }
}

/**
 * An operator function which deduplicates an occurrence stream. Occurrence
 * `duration` is currently ignored.
 */
function unique() {
  return options => new UniqueOperator([], options);
}
class UniqueOperator extends Operator {
  /** Not actually used but necessary for IRunnable interface */
  set(_, value) {
    return new UniqueOperator(
      [],
      Object.assign(Object.assign({}, this.config), {
        base: this.config.base && this.config.base.set('timezone', value),
        timezone: value,
      }),
    );
  }
  *_run(args = {}) {
    if (!this.config.base) return;
    const stream = new IterableWrapper(this.config.base, args);
    while (!stream.done) {
      const yieldArgs = yield this.normalizeRunOutput(stream.value);
      const lastValue = stream.value;
      // iterate the current stream
      stream.next(yieldArgs);
      while (
        !(yieldArgs && yieldArgs.skipToDate) &&
        !stream.done &&
        stream.value.isEqual(lastValue)
      ) {
        stream.next();
      }
    }
  }
  calculateIsInfinite() {
    return !!(this.config.base && this.config.base.isInfinite);
  }
  calculateHasDuration() {
    return !!(this.config.base && this.config.base.hasDuration);
  }
}

class Schedule extends ScheduleBase {
  /**
   * Create a new Schedule object with the specified options.
   *
   * The order of precidence for rrules, rdates, exrules, and exdates is:
   *
   * 1. rrules are included
   * 2. exrules are excluded
   * 3. rdates are included
   * 4. exdates are excluded
   *
   * ### Options
   *
   * - **timezone**: The timezone that yielded occurrences should be *displayed* in.
   *   Note, this one affects the *displayed* timezone of yielded occurrences.
   *   For rules, occurrences are first found using the unmodified rule
   *   config (including whatever timezone the `start` datetime is defined
   *   in), and then converted to the timezone specified here before being
   *   yielded. By default, the timezone is *local* time (`null`). So if you don't
   *   want your rules to be displayed in local time, you must supply a
   *   timezone argument.
   * - **data**: arbitrary data you can associate with this Schedule. This
   *   is the only mutable property of `Schedule` objects.
   * - **maxDuration**: currently unused.
   * - **rrules**: rules specifying when occurrences happen. See the "Rule Config"
   *   section below.
   * - **rdates**: individual dates that should be _included_ in the schedule.
   * - **exdates**: individual dates that should be _excluded_ from the schedule.
   * - **exrules**: rules specifying when occurrences shouldn't happen. See the
   *   "Rule Config" section below.
   *
   * ### Rule Config
   *
   * - #### frequency
   *
   *   The frequency rule part identifies the type of recurrence rule. Valid values
   *   include `"SECONDLY"`, `"MINUTELY"`, `"HOURLY"`, `"DAILY"`, `"WEEKLY"`,
   *   `"MONTHLY"`, or `"YEARLY"`.
   *
   * - #### start
   *
   *   The start of the rule (not necessarily the first occurrence).
   *   Either a `DateAdapter` instance, date object, or `DateTime` object.
   *   The type of date object depends on the `DateAdapter` class used for this
   *   `Rule`.
   *
   * - #### end?
   *
   *   The end of the rule (not necessarily the last occurrence).
   *   Either a `DateAdapter` instance, date object, or `DateTime` object.
   *   The type of date object depends on the `DateAdapter` class used for this
   *   `Rule`.
   *
   * - #### duration?
   *
   *   A length of time expressed in milliseconds.
   *
   * - #### interval?
   *
   *   The interval rule part contains a positive integer representing at
   *   which intervals the recurrence rule repeats. The default value is
   *   `1`, meaning every second for a SECONDLY rule, every minute for a
   *   MINUTELY rule, every hour for an HOURLY rule, every day for a
   *   DAILY rule, every week for a WEEKLY rule, every month for a
   *   MONTHLY rule, and every year for a YEARLY rule. For example,
   *   within a DAILY rule, a value of `8` means every eight days.
   *
   * - #### count?
   *
   *   The count rule part defines the number of occurrences at which to
   *   range-bound the recurrence. `count` and `end` are both two different
   *   ways of specifying how a recurrence completes.
   *
   * - #### weekStart?
   *
   *   The weekStart rule part specifies the day on which the workweek starts.
   *   Valid values are `"MO"`, `"TU"`, `"WE"`, `"TH"`, `"FR"`, `"SA"`, and `"SU"`.
   *   This is significant when a WEEKLY rule has an interval greater than 1,
   *   and a `byDayOfWeek` rule part is specified. The
   *   default value is `"MO"`.
   *
   * - #### bySecondOfMinute?
   *
   *   The bySecondOfMinute rule part expects an array of seconds
   *   within a minute. Valid values are 0 to 60.
   *
   * - #### byMinuteOfHour?
   *
   *   The byMinuteOfHour rule part expects an array of minutes within an hour.
   *   Valid values are 0 to 59.
   *
   * - #### byHourOfDay?
   *
   *   The byHourOfDay rule part expects an array of hours of the day.
   *   Valid values are 0 to 23.
   *
   * - #### byDayOfWeek?
   *
   *   *note: the byDayOfWeek rule part is kinda complex. Blame the ICAL spec.*
   *
   *   The byDayOfWeek rule part expects an array. Each array entry can
   *   be a day of the week (`"SU"`, `"MO"` , `"TU"`, `"WE"`, `"TH"`,
   *   `"FR"`, `"SA"`). If the rule's `frequency` is either MONTHLY or YEARLY,
   *   Any entry can also be a tuple where the first value of the tuple is a
   *   day of the week and the second value is an positive/negative integer
   *   (e.g. `["SU", 1]`). In this case, the number indicates the nth occurrence of
   *   the specified day within the MONTHLY or YEARLY rule.
   *
   *   The behavior of byDayOfWeek changes depending on the `frequency`
   *   of the rule.
   *
   *   Within a MONTHLY rule, `["MO", 1]` represents the first Monday
   *   within the month, whereas `["MO", -1]` represents the last Monday
   *   of the month.
   *
   *   Within a YEARLY rule, the numeric value in a byDayOfWeek tuple entry
   *   corresponds to an offset within the month when the byMonthOfYear rule part is
   *   present, and corresponds to an offset within the year otherwise.
   *
   *   Regardless of rule `frequency`, if a byDayOfWeek entry is a string
   *   (rather than a tuple), it means "all of these days" within the specified
   *   frequency (e.g. within a MONTHLY rule, `"MO"` represents all Mondays within
   *   the month).
   *
   * - #### byDayOfMonth?
   *
   *   The byDayOfMonth rule part expects an array of days
   *   of the month. Valid values are 1 to 31 or -31 to -1.
   *
   *   For example, -10 represents the tenth to the last day of the month.
   *   The byDayOfMonth rule part *must not* be specified when the rule's
   *   `frequency` is set to WEEKLY.
   *
   * - #### byMonthOfYear?
   *
   *   The byMonthOfYear rule part expects an array of months
   *   of the year. Valid values are 1 to 12.
   *
   */
  constructor(options = {}) {
    super(options);
    this.rrules = [];
    this.exrules = [];
    for (const prop of ['rrules', 'exrules']) {
      const arg = options[prop];
      if (arg) {
        this[prop] = arg.map(ruleArgs => {
          if (ruleArgs instanceof Rule) {
            return ruleArgs.set('timezone', this.timezone);
          } else {
            return new Rule(ruleArgs, {
              timezone: this.timezone,
            });
          }
        });
      }
    }
    for (const prop of ['rdates', 'exdates']) {
      const arg = options[prop];
      if (arg) {
        this[prop] =
          arg instanceof Dates
            ? arg.set('timezone', this.timezone)
            : new Dates({
                dates: arg,
                timezone: this.timezone,
              });
      } else {
        this[prop] = new Dates({
          timezone: this.timezone,
        });
      }
    }
    this.hasDuration =
      this.rrules.every(rule => rule.hasDuration) &&
      this.exrules.every(rule => rule.hasDuration) &&
      this.rdates.hasDuration &&
      this.exdates.hasDuration;
    this.isInfinite = this.rrules.some(rule => rule.isInfinite);
    this.occurrenceStream = [
      add(...this.rrules),
      subtract(...this.exrules),
      add(this.rdates),
      subtract(this.exdates),
      unique(),
    ].reduce((prev, curr) => curr({ base: prev, timezone: this.timezone }), undefined);
  }
  add(prop, value) {
    const rrules = this.rrules.slice();
    const exrules = this.exrules.slice();
    let rdates = this.rdates;
    let exdates = this.exdates;
    switch (prop) {
      case 'rrule':
        rrules.push(value);
        break;
      case 'exrule':
        exrules.push(value);
        break;
      case 'rdate':
        rdates = this.rdates.add(value);
        break;
      case 'exdate':
        exdates = this.exdates.add(value);
        break;
    }
    return new Schedule({
      timezone: this.timezone,
      data: this.data,
      rrules,
      exrules,
      rdates,
      exdates,
    });
  }
  remove(prop, value) {
    let rrules = this.rrules;
    let exrules = this.exrules;
    let rdates = this.rdates;
    let exdates = this.exdates;
    switch (prop) {
      case 'rrule':
        rrules = rrules.filter(rule => rule !== value);
        break;
      case 'exrule':
        exrules = exrules.filter(rule => rule !== value);
        break;
      case 'rdate':
        rdates = this.rdates.remove(value);
        break;
      case 'exdate':
        exdates = this.exdates.remove(value);
        break;
    }
    return new Schedule({
      timezone: this.timezone,
      data: this.data,
      rrules,
      exrules,
      rdates,
      exdates,
    });
  }
  set(prop, value, options = {}) {
    let timezone = this.timezone;
    let rrules = this.rrules;
    let exrules = this.exrules;
    let rdates = this.rdates;
    let exdates = this.exdates;
    switch (prop) {
      case 'timezone':
        if (value === this.timezone && !options.keepLocalTime) return this;
        else if (options.keepLocalTime) {
          rrules = rrules.map(rule => rule.set('timezone', value, options));
          exrules = exrules.map(rule => rule.set('timezone', value, options));
          rdates = rdates.set('timezone', value, options);
          exdates = exdates.set('timezone', value, options);
        }
        timezone = value;
        break;
      case 'rrules':
        rrules = value;
        break;
      case 'exrules':
        exrules = value;
        break;
      case 'rdates':
        rdates = value;
        break;
      case 'exdates':
        exdates = value;
        break;
    }
    return new Schedule({
      timezone,
      data: this.data,
      rrules,
      exrules,
      rdates,
      exdates,
    });
  }
}

/**
 * An operator function, which takes a spread of occurrence generators and only
 * returns the dates which intersect every occurrence generator.
 *
 * Because it's possible for all the generators to never intersect,
 * and because the intersection operator can't detect this lack of intersection,
 * you must call `intersection()` with a `{maxFailedIterations: number}` argument.
 * For convenience, you can globally set `RScheduleConfig.defaultMaxFailedIterations`.
 * Without further information, I'd probably set `defaultMaxFailedIterations = 50`.
 *
 * The `maxFailedIterations` argument caps the number of iterations the operator will
 * run through without finding a single valid occurrence. If this number is reached, the operator will
 * stop iterating (preventing a possible infinite loop).
 *
 * - Note: `maxFailedIterations` caps the number of iterations which
 *   *fail to turn up a single valid occurrence*. Every time a valid occurrence is returned,
 *   the current iteration count is reset to 0.
 *
 */
function intersection(args) {
  return options => new IntersectionOperator(args, options);
}
class IntersectionOperator extends Operator {
  constructor(args, config) {
    super(args.streams, config);
    if (this.isInfinite) {
      this.maxFailedIterations =
        args.maxFailedIterations || IntersectionOperator.defaultMaxFailedIterations;
      if (!this.maxFailedIterations) {
        throw new ArgumentError(
          'The IntersectionOperator must be provided ' +
            'a `maxFailedIterations` argument when it is built from schedules of infinite length. ' +
            'This argument is used to ensure that the IntersectionOperator does not enter ' +
            'an infinite loop because the underlying schedules never intersect. ' +
            'If the `maxFailedIterations` count is reached it will be assumed that ' +
            'all valid occurrences have been found and iteration will end without error.' +
            'Without additional information, "50" is probably a good ' +
            '`maxFailedIterations` value. ' +
            'If the schedules are not of infinite length, `maxFailedIterations` is ignored. ' +
            'Note also that you can provide a `defaultMaxFailedIterations` number via ' +
            'IntersectionOperator.defaultMaxFailedIterations.',
        );
      }
    }
  }
  set(_, value) {
    return new IntersectionOperator(
      {
        maxFailedIterations: this.maxFailedIterations,
        streams: this.streams.map(stream => stream.set('timezone', value)),
      },
      Object.assign(Object.assign({}, this.config), {
        base: this.config.base && this.config.base.set('timezone', value),
        timezone: value,
      }),
    );
  }
  *_run(args = {}) {
    const streams = this.streams.map(stream => new IterableWrapper(stream, args));
    if (this.config.base) {
      streams.push(new IterableWrapper(this.config.base, args));
    }
    if (streams.length === 0) return;
    const hasEndDate = !!(!this.isInfinite || args.reverse || args.end);
    if (
      !cycleStreams$1(
        streams,
        undefined,
        Object.assign(Object.assign({}, args), {
          hasEndDate,
          iteration: 0,
          maxIterations: this.maxFailedIterations,
        }),
      )
    ) {
      return;
    }
    let stream = selectNextIterable(streams, args);
    while (stream) {
      const yieldArgs = yield this.normalizeRunOutput(stream.value);
      const lastValidDate = stream.value;
      if (!(yieldArgs === null || yieldArgs === void 0 ? void 0 : yieldArgs.skipToDate)) {
        // iterate the current stream
        stream.next();
      }
      if (
        !cycleStreams$1(
          streams,
          lastValidDate,
          Object.assign(Object.assign({}, args), {
            hasEndDate,
            iteration: 0,
            maxIterations: this.maxFailedIterations,
          }),
          yieldArgs,
        )
      ) {
        return;
      }
      // The call to `cycleStreams()`, above, has already called
      // selectNextIterable with the `yieldArgs`
      stream = selectNextIterable(streams, args);
    }
  }
  calculateIsInfinite() {
    // Note: Array#every() === true when length === 0
    if (!this.config.base) {
      if (this.streams.length === 0) return false;
      return this.streams.every(stream => stream.isInfinite);
    } else if (this.streams.length === 0) return this.config.base.isInfinite;
    return this.config.base.isInfinite && this.streams.every(stream => stream.isInfinite);
  }
  calculateHasDuration() {
    const streamsDuration = this.streams.every(stream => stream.hasDuration);
    if (!this.config.base) return streamsDuration;
    return this.config.base.hasDuration && streamsDuration;
  }
}
function cycleStreams$1(streams, lastValidDate, options, yieldArgs) {
  const next = selectNextIterable(streams, options, yieldArgs);
  if (!next) return false;
  if (lastValidDate && next.value.isEqual(lastValidDate)) return true;
  if (streams.some(stream => stream.done)) return false;
  if (streams.every(stream => stream.value.isEqual(next.value))) return true;
  options.iteration++;
  if (options.maxIterations && !options.hasEndDate && options.iteration > options.maxIterations) {
    return false;
  }
  // Since not all of the streams are equal, we grab the last stream...
  const last = selectLastIterable(streams, options);
  // ...and skip all the other streams so they are equal or past the last one
  streams.forEach(stream => {
    // Because streams can have multiple, identical dates in a row,
    // we don't want to `skipToDate` if the provided date is equal to the current date.
    if (last.value.isEqual(stream.value)) return;
    stream.next({ skipToDate: last.value });
  });
  // then we repeat
  return cycleStreams$1(streams, lastValidDate, options);
}

class DurationIterableWrapper extends IterableWrapper {
  constructor(generator, runArgs) {
    super(generator, runArgs);
    this.workingValue = this.value;
    this.next();
  }
}
class MergeDurationOperatorError extends Error {}
/**
 * An operator function which takes an occurrence stream with
 * `hasDuration === true` and merges occurrences which have overlapping
 * start and end times.
 *
 * Because it's possible for all the occurrences in the stream to have
 * overlapping start and end times, you must provide a `maxDuration`
 * argument that represents the maximum possible duration for a single
 * occurrence. If this duration is exceeded, a `MergeDurationOperatorError`
 * will be thrown.
 *
 * - For your convenience, you can globally set a default
 *   `MergeDurationOperator#maxDuration` via
 *   `RScheduleConfig.MergeDurationOperator.defaultMaxDuration`.
 *
 * Usage example:
 *
 * ```typescript
 * const MILLISECONDS_IN_HOUR = 1000 * 60 * 60;
 *
 * const dates = new Dates({
 *   dates: [
 *     new StandardDateAdapter(new Date(2010, 10, 10, 13), { duration: MILLISECONDS_IN_HOUR * 1 }),
 *     new StandardDateAdapter(new Date(2010, 10, 11, 13), { duration: MILLISECONDS_IN_HOUR * 2 }),
 *     new StandardDateAdapter(new Date(2010, 10, 11, 14), { duration: MILLISECONDS_IN_HOUR * 2 }),
 *     new StandardDateAdapter(new Date(2010, 10, 12, 13), { duration: MILLISECONDS_IN_HOUR * 1 }),
 *   ],
 *   dateAdpter: StandardDateAdapter,
 * }).pipe(
 *   mergeDuration({
 *     maxDuration: MILLISECONDS_IN_HOUR * 24
 *   })
 * )
 *
 * dates.occurrences().toArray() === [
 *   new StandardDateAdapter(new Date(2010, 10, 10, 13), { duration: MILLISECONDS_IN_HOUR * 1 }),
 *   new StandardDateAdapter(new Date(2010, 10, 11, 13), { duration: MILLISECONDS_IN_HOUR * 3 }),
 *   new StandardDateAdapter(new Date(2010, 10, 12, 13), { duration: MILLISECONDS_IN_HOUR * 1 }),
 * ]
 * ```
 */
function mergeDuration(args) {
  return options => new MergeDurationOperator(args, options);
}
class MergeDurationOperator extends Operator {
  constructor(args, config) {
    super([], config);
    this.maxDuration = args.maxDuration;
    if (config.base && !config.base.hasDuration) {
      throw new ArgumentError(
        'Base stream provided to MergeDurationOperator does not have an associated duration. ' +
          'The MergeDurationOperator can only be used with streams which have a duration. ',
      );
    }
  }
  /** Not actually used but necessary for IRunnable interface */
  set(_, value) {
    return new MergeDurationOperator(
      {
        maxDuration: this.maxDuration,
      },
      Object.assign(Object.assign({}, this.config), {
        base: this.config.base && this.config.base.set('timezone', value),
        timezone: value,
      }),
    );
  }
  _run(args = {}) {
    return args.reverse ? this.reverseRun(args) : this.forwardRun(args);
  }
  calculateIsInfinite() {
    return !!(this.config.base && this.config.base.isInfinite);
  }
  calculateHasDuration() {
    return true;
  }
  *forwardRun(args = {}) {
    if (!this.config.base) return;
    // We want to find occurrences that end after the provided
    // `start` time even if they begin before the provided `start`
    // time. Because of this, we must begin iterating `maxDuration`
    // before the provided start time.
    let checkFromStart = args.start;
    if (args.start) {
      checkFromStart = args.start.subtract(this.maxDuration, 'millisecond');
    }
    let checkFromEnd = args.end;
    if (args.end) {
      checkFromEnd = args.end.add(this.maxDuration, 'millisecond');
    }
    const stream = new DurationIterableWrapper(
      this.config.base,
      Object.assign(Object.assign({}, args), { start: checkFromStart, end: checkFromEnd }),
    );
    let yieldArgs;
    // checking `stream.workingValue` because when `stream.done === true`
    // `stream.workingValue` will not have been yielded yet
    while (stream.workingValue) {
      // TODO(@john.carroll.p): figure out how to handle `DateTime#generators` for merged `DateTimes`
      while (!stream.done && stream.workingValue.end.isAfterOrEqual(stream.value)) {
        if (stream.workingValue.duration > this.maxDuration) {
          throw new MergeDurationOperatorError(
            `MergeDurationOperatorError: Occurrence duration exceeded maxDuration of ` +
              this.maxDuration,
          );
        }
        if (stream.value.end.isAfter(stream.workingValue.end)) {
          const diff = stream.value.end.valueOf() - stream.workingValue.end.valueOf();
          stream.workingValue = stream.workingValue.set(
            'duration',
            stream.workingValue.duration + diff,
          );
        }
        stream.next();
      }
      // check to make sure the occurrence we are about to yield ends after the
      // provided start time.
      if (args.start && stream.workingValue.end.isBefore(args.start)) {
        stream.workingValue = stream.value;
        stream.next();
        continue;
      }
      // make sure the occurrence we are about to yield ends after the
      // provided skipToDate
      if (
        (yieldArgs === null || yieldArgs === void 0 ? void 0 : yieldArgs.skipToDate) &&
        stream.workingValue.end.isBefore(yieldArgs.skipToDate)
      ) {
        stream.workingValue = stream.value;
        stream.next();
        continue;
      }
      // make sure we are not after the user requested `end` time.
      if (args.end && stream.workingValue && stream.workingValue.isAfter(args.end)) {
        break;
      }
      if (stream.workingValue.duration > this.maxDuration) {
        throw new MergeDurationOperatorError(
          `MergeDurationOperatorError: Occurrence duration exceeded maxDuration of ` +
            this.maxDuration,
        );
      }
      yieldArgs = yield this.normalizeRunOutput(stream.workingValue);
      if (
        (yieldArgs === null || yieldArgs === void 0 ? void 0 : yieldArgs.skipToDate) &&
        stream.workingValue.isAfterOrEqual(yieldArgs.skipToDate)
      ) {
        throw new Error(
          'A provided `skipToDate` option must be greater than the last yielded date ' +
            '(or smaller, in the case of reverse iteration)',
        );
      }
      stream.workingValue = stream.value;
      stream.next();
    }
  }
  *reverseRun(args = {}) {
    if (!this.config.base) return;
    // We want to find occurrences that end after the provided
    // `start` time even if they begin before the provided `start`
    // time. Because of this, we must begin iterating `maxDuration`
    // before the provided start time.
    let checkFromStart = args.start;
    if (args.start) {
      checkFromStart = args.start.subtract(this.maxDuration, 'millisecond');
    }
    let checkFromEnd = args.end;
    if (args.end) {
      checkFromEnd = args.end.add(this.maxDuration, 'millisecond');
    }
    const stream = new DurationIterableWrapper(
      this.config.base,
      Object.assign(Object.assign({}, args), { start: checkFromStart, end: checkFromEnd }),
    );
    let yieldArgs;
    // checking `stream.workingValue` because when `stream.done === true`
    // `stream.workingValue` will not have been yielded yet
    while (stream.workingValue) {
      // TODO(@john.carroll.p): figure out how to handle `DateTime#generators` for merged `DateTimes`
      while (!stream.done && stream.workingValue.isBeforeOrEqual(stream.value.end)) {
        if (stream.workingValue.duration > this.maxDuration) {
          throw new MergeDurationOperatorError(
            `MergeDurationOperatorError: Occurrence duration exceeded maxDuration of ` +
              this.maxDuration,
          );
        }
        if (
          stream.value.isBefore(stream.workingValue) ||
          stream.value.end.isAfter(stream.workingValue.end)
        ) {
          if (stream.value.end.isAfter(stream.workingValue.end)) {
            // `stream.workingValue` is a subset of `stream.value`
            // so simply replace `stream.workingValue` with `stream.value`
            stream.workingValue = stream.value;
          } else {
            const diff = stream.workingValue.valueOf() - stream.value.valueOf();
            stream.workingValue = stream.value.set('duration', stream.workingValue.duration + diff);
          }
        }
        stream.next();
      }
      // check to make sure the occurrence we are about to yield starts before the
      // provided start time.
      if (args.start && stream.workingValue.end.isBefore(args.start)) {
        break;
      }
      if (
        yieldArgs &&
        yieldArgs.skipToDate &&
        stream.workingValue.end.isBefore(yieldArgs.skipToDate)
      ) {
        stream.workingValue = stream.value;
        stream.next();
        continue;
      }
      // make sure we are not after the user requested `end` time.
      if (args.end && stream.workingValue && stream.workingValue.isAfter(args.end)) {
        stream.workingValue = stream.value;
        stream.next();
        continue;
      }
      if (stream.workingValue.duration > this.maxDuration) {
        throw new MergeDurationOperatorError(
          `MergeDurationOperatorError: Occurrence duration exceeded maxDuration of ` +
            this.maxDuration,
        );
      }
      yieldArgs = yield this.normalizeRunOutput(stream.workingValue);
      if (
        yieldArgs &&
        yieldArgs.skipToDate &&
        stream.workingValue.end.isBeforeOrEqual(yieldArgs.skipToDate)
      ) {
        throw new Error(
          'A provided `skipToDate` option must be greater than the last yielded date ' +
            '(or smaller, in the case of reverse iteration)',
        );
      }
      stream.workingValue = stream.value;
      stream.next();
    }
  }
}

class SplitDurationOperatorError extends Error {}
/**
 * An operator function which takes an occurrence stream with
 * `hasDuration === true` and passes occurrences through a splitting
 * function. One usecase for this operator is to dynamically break up
 * occurrences with a large duration into several smaller occurrences.
 *
 * You must provide a `maxDuration` argument that represents the
 * maximum possible duration for a single occurrence. If this
 * duration is exceeded, a `SplitDurationOperatorError` will be
 * thrown.
 *
 * - For your convenience, you can globally set a default
 *   `SplitDurationOperator#maxDuration` via
 *   `RScheduleConfig.SplitDurationOperator.defaultMaxDuration`.
 *
 * Usage example:
 *
 * ```typescript
 * const MILLISECONDS_IN_HOUR = 1000 * 60 * 60;
 *
 * const splitFn = (date: DateTime) => {
 *   if (date.duration > MILLISECONDS_IN_HOUR) {
 *     const diff = date.duration! / 2;
 *
 *     return [
 *       date.set('duration', diff),
 *       date.add(diff, 'millisecond').set('duration', diff),
 *     ];
 *   }
 *
 *   return [date];
 * };
 *
 * const dates = new Dates({
 *   dates: [
 *     new StandardDateAdapter(new Date(2010, 10, 10, 13), { duration: MILLISECONDS_IN_HOUR * 1 }),
 *     new StandardDateAdapter(new Date(2010, 10, 11, 13), { duration: MILLISECONDS_IN_HOUR * 2 }),
 *   ],
 *   dateAdpter: StandardDateAdapter,
 * }).pipe(
 *   splitDuration({
 *     splitFn,
 *     maxDuration: MILLISECONDS_IN_HOUR * 1
 *   })
 * )
 *
 * expect(dates.occurrences().toArray()).toEqual([
 *   new StandardDateAdapter(new Date(2010, 10, 10, 13), { duration: MILLISECONDS_IN_HOUR * 1 }),
 *   new StandardDateAdapter(new Date(2010, 10, 11, 13), { duration: MILLISECONDS_IN_HOUR * 1 }),
 *   new StandardDateAdapter(new Date(2010, 10, 11, 14), { duration: MILLISECONDS_IN_HOUR * 1 }),
 * ])
 * ```
 */
function splitDuration(args) {
  return options => new SplitDurationOperator(args, options);
}
class SplitDurationOperator extends Operator {
  constructor(args, config) {
    super([], config);
    this.splitFn = args.splitFn;
    this.maxDuration = args.maxDuration;
    if (config.base && !config.base.hasDuration) {
      throw new ArgumentError(
        'Base stream provided to SplitDurationOperator does not have an associated duration. ' +
          'The SplitDurationOperator can only be used with streams which have a duration.',
      );
    }
  }
  /** Not actually used but necessary for IRunnable interface */
  set(_, value) {
    return new SplitDurationOperator(
      {
        maxDuration: this.maxDuration,
        splitFn: this.splitFn,
      },
      Object.assign(Object.assign({}, this.config), {
        base: this.config.base && this.config.base.set('timezone', value),
        timezone: value,
      }),
    );
  }
  *_run(args = {}) {
    if (!this.config.base) return;
    const reverse = args.reverse || false;
    // We want to find occurrences that end after the provided
    // `start` time even if they begin before the provided `start`
    // time. Because of this, we add `maxDuration` to
    // the provided start time.
    let checkFromStart = args.start;
    if (args.start) {
      checkFromStart = args.start.subtract(this.maxDuration, 'millisecond');
    }
    // same goes for `end` time as with `start` time.
    let checkFromEnd = args.end;
    if (args.end) {
      checkFromEnd = args.end.add(this.maxDuration, 'millisecond');
    }
    const stream = new IterableWrapper(
      this.config.base,
      Object.assign(Object.assign({}, args), { start: checkFromStart, end: checkFromEnd }),
    );
    let yieldArgs;
    const datesBucket = [];
    while (!stream.done || (datesBucket[0] && datesBucket[0][0])) {
      /**
       * Example:
       * 10am - 2pm -> 10am - 12pm, 12pm - 2pm
       * 11am - 3pm -> 11am - 1pm, 1pm - 3pm
       * 2pm - 4pm -> 2pm - 3pm, 3pm - 4pm
       */
      if (!(datesBucket[0] && datesBucket[0][0])) {
        // we're out of dates
        datesBucket.push(this.splitDate(stream.value, reverse));
        stream.next();
      }
      while (
        !stream.done &&
        (reverse
          ? datesBucket[0].some(date => date.isBeforeOrEqual(stream.value.end))
          : datesBucket[0].some(date => date.isAfterOrEqual(stream.value)))
      ) {
        datesBucket.push(this.splitDate(stream.value, reverse));
        stream.next();
      }
      let selectedDate = datesBucket[0] && datesBucket[0][0];
      let bucketIndex = -1;
      let selectedBucketIndex = 0;
      let dateIndex = -1;
      let selectedDateIndex = 0;
      // find the next date as well as its location in the datesBucket
      for (const bucket of datesBucket) {
        bucketIndex++;
        dateIndex = -1;
        for (const date of bucket) {
          dateIndex++;
          let dateShouldComeNext;
          if (reverse) {
            dateShouldComeNext =
              date.isAfter(selectedDate) ||
              (date.isEqual(selectedDate) && date.duration > selectedDate.duration);
          } else {
            dateShouldComeNext =
              date.isBefore(selectedDate) ||
              (date.isEqual(selectedDate) && date.duration < selectedDate.duration);
          }
          if (dateShouldComeNext) {
            selectedDate = date;
            selectedBucketIndex = bucketIndex;
            selectedDateIndex = dateIndex;
            break;
          }
        }
      }
      datesBucket[selectedBucketIndex].splice(selectedDateIndex, 1);
      if (datesBucket[selectedBucketIndex].length === 0) {
        datesBucket.splice(selectedBucketIndex, 1);
      }
      // If we've been yieldedArgs from the last cycle, check to see
      // that the selectedDate honors the `skipToDate` requirement
      // if not, discard this selectedDate
      if (
        yieldArgs &&
        yieldArgs.skipToDate &&
        selectedDate &&
        !datePastEnd(selectedDate, args) &&
        !datePastSkipToDate(selectedDate, yieldArgs.skipToDate, args)
      ) {
        continue;
      }
      // because we subtracted `maxDuration` to the base iterator's start time,
      // check to make sure the selectedDate we are about to yield should
      // actually be yielded (it may be before the provided `start` time).
      // If not, discard the selectedDate.
      if (args.start && selectedDate.end.isBefore(args.start)) {
        if (reverse) break;
        continue;
      }
      // because we added `maxDuration` to the base iterator's end time,
      // check to make sure the selectedDate we are about to yield should
      // actually be yielded (it may be after the provided `end` time).
      // If not, end iteration.
      if (args.end && selectedDate.isAfter(args.end)) {
        if (reverse) continue;
        break;
      }
      if (selectedDate.duration > this.maxDuration) {
        throw new SplitDurationOperatorError(
          `SplitDurationOperatorError: Occurrence duration exceeded maxDuration of ` +
            this.maxDuration,
        );
      }
      yieldArgs = yield this.normalizeRunOutput(selectedDate);
      if (
        yieldArgs &&
        yieldArgs.skipToDate &&
        (args.reverse
          ? selectedDate.isBeforeOrEqual(yieldArgs.skipToDate)
          : selectedDate.isAfterOrEqual(yieldArgs.skipToDate))
      ) {
        throw new Error(
          'A provided `skipToDate` option must be greater than the last yielded date ' +
            '(or smaller, in the case of reverse iteration)',
        );
      }
    }
  }
  calculateIsInfinite() {
    return !!(this.config.base && this.config.base.isInfinite);
  }
  calculateHasDuration() {
    return true;
  }
  splitDate(date, reverse) {
    const dates = this.splitFn(date);
    let valid;
    if (dates.length === 0) {
      valid = false;
    } else if (dates.length === 1) {
      valid = date.duration === dates[0].duration;
    } else {
      valid = date.duration === dates.reduce((prev, curr) => prev + curr.duration, 0);
    }
    if (!valid) {
      throw new Error(
        'The provided SplitDurationOperator split function ' +
          'must return an array of DateTimes with length > 0 ' +
          'where the total duration of the new dates equals the duration of ' +
          'the original date.',
      );
    }
    dates.sort(dateTimeSortComparer);
    if (reverse) {
      dates.reverse();
    }
    return dates;
  }
}
function datePastEnd(date, options) {
  return !!(options.reverse
    ? options.start && date.isBefore(options.start)
    : options.end && date.isAfter(options.end));
}
function datePastSkipToDate(date, skipToDate, options) {
  return !!(options.reverse ? skipToDate.isAfterOrEqual(date) : skipToDate.isBeforeOrEqual(date));
}

export {
  AddOperator,
  Calendar,
  Collection,
  CollectionIterator,
  Dates,
  IntersectionOperator,
  MergeDurationOperator,
  MergeDurationOperatorError,
  OccurrenceGenerator,
  OccurrenceIterator,
  Operator,
  Rule,
  RuleBase,
  Schedule,
  ScheduleBase,
  SplitDurationOperator,
  SplitDurationOperatorError,
  SubtractOperator,
  UniqueOperator,
  add,
  intersection,
  mergeDuration,
  splitDuration,
  subtract,
  unique,
};
